/**
 * Leads list (task 016-05; widened by epic 024 task 14; email search 043-03)
 * — read-only view of captured leads plus the rejected-captures view.
 *
 * Per task scope:
 * - Shows: Email, Campaign (with the CTO link label underneath), Status
 *   (Success / Duplicate / — (not checked)), Journey, Country, Created
 * - A "Rejected" toggle swaps the table to the capture-rejections list
 *   (email, campaign, site, reason, time, test flag)
 * - An email search box filters the table server-side (task 043-03): the
 *   typed address hits GET /admin/leads?email=… (hashed to the indexed
 *   email_hash); clearing it restores the unfiltered list
 * - Clicking a lead row opens its detail view (task 043-03, /leads/:id);
 *   the table stays read-only otherwise
 * - Newest-first ordering from API
 *
 * Epic 088 task 02 additions (leads view only):
 * - A date control (All dates / Today / Yesterday / Custom — the client's
 *   America/New_York day), a traffic-source select, the "Show test leads"
 *   toggle (default off) and Export all / Export unique buttons that download
 *   the api's `export.csv` for the current filters; the footer shows people
 *   beside leads
 * - Any filter change refetches the list; an email search ignores the
 *   filters (an exact-address lookup across all dates, sources and test
 *   leads) and disables the filter controls and both exports until cleared
 * - Journey status nullable per contract (render empty when null)
 * - Status null renders "— (not checked)": the lead passed both checks but
 *   the Beehiiv verdict is unavailable (unconfigured / off / failed lookup);
 *   a co-op pass-through lead is never checked by design (FR-A35, ADR-007
 *   §Amendment (c))
 *
 * Follows admin-requirements.md §1.4 conventions: MM-DD-YYYY dates,
 * plain-language headers, no visible DB ids.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SiteResponse } from '@leads-platform/shared';
import { api, ApiError, CaptureRejectionRow, LeadListRow } from '../lib/api';
import { PageHeader } from '../components/ui/page-header';
import { Card } from '../components/ui/card';
import { Tag } from '../components/ui/tag';
import { Toggle } from '../components/ui/toggle';
import { SearchInput } from '../components/ui/search-input';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { DateRangeField } from '../components/date-range-field';
import { TestLeadsToggle } from './reports/report-filters';
import { fmtDate } from '../lib/format';
import { searchLeadsByEmail } from './lead-detail';

/**
 * Beehiiv lookup status badge (task 024-14, FR-A35's "Status" column).
 * Null/absent renders the literal "— (not checked)" — the badge mapping for
 * the DB-null case (epic 024 README #20).
 */
function LookupStatusCell({ status }: { status?: LeadListRow['beehiivLookupStatus'] }) {
  switch (status) {
    case 'success':
      return <Tag tone="sky">Success</Tag>;
    case 'duplicate':
      return <Tag tone="amber">Duplicate</Tag>;
    default:
      return <span className="text-faint">— (not checked)</span>;
  }
}

/**
 * Journey status badge component.
 * Returns null when status is undefined/null (per contract).
 */
function JourneyStatusCell({ status }: { status?: string | null }) {
  if (!status) return <span className="text-faint">--</span>;

  const toneMap: Record<string, 'sky' | 'amber' | 'rose'> = {
    running: 'sky',
    completed: 'amber',
    failed: 'rose',
  };
  const tone = toneMap[status] || 'sky';
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  return <Tag tone={tone}>{label}</Tag>;
}

/**
 * Campaign cell shared by both tables — the campaign name with the CTO link
 * label ("<site label> · <promo slug>") underneath when the capture came
 * through a link (task 024-14).
 */
function CampaignCell({ name, linkLabel }: { name: string | null; linkLabel?: string }) {
  return (
    <td className="px-4 py-3 text-muted">
      {name || <span className="text-faint">--</span>}
      {linkLabel ? <span className="block text-xs text-faint">{linkLabel}</span> : null}
    </td>
  );
}

/**
 * Test badge component — shows "Test" when isTest is true.
 */
function TestBadge({ isTest }: { isTest: boolean }) {
  if (!isTest) return null;
  return (
    <span className="ml-2 rounded-full bg-amber/10 px-2 py-0.5 text-xs font-medium text-amber">
      Test
    </span>
  );
}

type LeadsPreset = 'all' | 'today' | 'yesterday' | 'custom';

const LEADS_PRESETS: readonly [LeadsPreset, string][] = [
  ['all', 'All dates'],
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['custom', 'Custom'],
];

type ExportVariant = 'all' | 'unique';

const EXPORT_BUTTONS: readonly [ExportVariant, string][] = [
  ['all', 'Export all'],
  ['unique', 'Export unique'],
];

/** The client's day — every date filter and export bound is an ET calendar day. */
const CLIENT_TIME_ZONE = 'America/New_York';

/** Today in `America/New_York`, `yyyy-MM-dd` (the api's `date-range.ts` idiom). */
function clientToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: CLIENT_TIME_ZONE });
}

/** The calendar day before `ymd`: arithmetic at UTC noon, never local midnight. */
function previousDay(ymd: string): string {
  const day = new Date(`${ymd}T12:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}

/** Preset strip — `PresetStrip`'s look with the Leads presets. */
function LeadsPresetStrip({
  preset,
  onChange,
}: {
  preset: LeadsPreset;
  onChange: (p: LeadsPreset) => void;
}) {
  return (
    <div className="flex gap-1">
      {LEADS_PRESETS.map(([p, label]) => (
        <button
          key={p}
          type="button"
          data-testid={`leads-preset-${p}`}
          onClick={() => onChange(p)}
          className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
            preset === p ? 'bg-accent text-white' : 'bg-paper text-faint hover:text-ink hairline'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<LeadListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [people, setPeople] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Email search state (task 043-03) — the search replaces the table contents
  // until cleared; "searching" covers the in-flight request.
  const [searchValue, setSearchValue] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [searching, setSearching] = useState(false);

  // Filter state (epic 088 D9). `from` / `to` are '' under All dates; the list
  // sends dates only as a pair, so a half-picked Custom range sends none.
  const [preset, setPreset] = useState<LeadsPreset>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [source, setSource] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [sites, setSites] = useState<SiteResponse[]>([]);

  // Every list load (filter effect, clear, search) takes a number; only the
  // latest response is applied, so a filter response can't overwrite search
  // results or the reverse.
  const requestSeq = useRef(0);
  // True while an email search is in flight or active — the filter effect
  // reads it here instead of depending on it, so clearing a search never
  // re-fires the effect (`clearSearch` owns the clear-time refetch).
  const searchBusy = useRef(false);

  // Rejected view state (task 024-14) — fetched lazily on the first toggle,
  // then kept for the session.
  const [showRejected, setShowRejected] = useState(false);
  const [rejections, setRejections] = useState<CaptureRejectionRow[]>([]);
  const [rejectionsTotal, setRejectionsTotal] = useState(0);
  const [rejectionsError, setRejectionsError] = useState<string | null>(null);
  const [rejectionsLoading, setRejectionsLoading] = useState(false);

  const rangeComplete = from !== '' && to !== '';
  const rangeHalfSet = preset === 'custom' && (from === '') !== (to === '');
  const filtersLocked = searching || searchActive;

  /** The list's filters: dates as a pair or not at all (D3), test off by default. */
  const listFilters = () => ({
    ...(rangeComplete ? { from, to } : {}),
    ...(source !== '' ? { source } : {}),
    includeTest,
  });

  /** Load the list through the filtered path; a superseded response is dropped. */
  const loadFiltered = () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    api
      .getLeads(listFilters())
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setLeads(response.rows);
        setTotal(response.total);
        setPeople(response.people);
      })
      .catch((err: ApiError) => {
        if (seq === requestSeq.current) setError(err.message);
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  };

  useEffect(() => {
    searchBusy.current = searching || searchActive;
  }, [searching, searchActive]);

  // Traffic-source options — best-effort: a failed load leaves the two fixed ones.
  useEffect(() => {
    api
      .getSites()
      .then(setSites)
      .catch(() => setSites([]));
  }, []);

  // Load leads on mount and whenever a filter changes (the mount load is this
  // effect's first run). A search in flight/active and a half-picked Custom
  // range wait.
  useEffect(() => {
    if (searchBusy.current || rangeHalfSet) return;
    loadFiltered();
  }, [from, to, source, includeTest]);

  /** Clear the email search and restore the list through the filtered path. */
  const clearSearch = () => {
    setSearchValue('');
    setSearchActive(false);
    setSearching(false);
    setError(null);
    loadFiltered();
  };

  /** Run the email search (task 043-03): the typed address is sent as the
   * `email` query param; the api normalises + hashes it against email_hash.
   * The filter controls lock from the submit on (D9). */
  const runSearch = (event: FormEvent) => {
    event.preventDefault();
    const email = searchValue.trim();
    if (email === '') {
      clearSearch();
      return;
    }
    const seq = ++requestSeq.current;
    setSearching(true);
    setError(null);
    searchLeadsByEmail(email)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setLeads(response.rows);
        setTotal(response.total);
        setPeople(response.people);
        setSearchActive(true);
        // A filter load the search superseded never lands to clear this.
        setLoading(false);
      })
      .catch((err: ApiError) => {
        if (seq === requestSeq.current) setError(err.message);
      })
      .finally(() => {
        if (seq === requestSeq.current) setSearching(false);
      });
  };

  const applyPreset = (next: LeadsPreset) => {
    setPreset(next);
    if (next === 'all') {
      setFrom('');
      setTo('');
    } else if (next === 'today') {
      const today = clientToday();
      setFrom(today);
      setTo(today);
    } else if (next === 'yesterday') {
      const yesterday = previousDay(clientToday());
      setFrom(yesterday);
      setTo(yesterday);
    }
  };

  const setRange = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom);
    setTo(nextTo);
  };

  /** Download the export for the current filters. */
  const exportLeads = (variant: ExportVariant) => {
    window.open(
      api.leadsExportUrl({ ...(rangeComplete ? { from, to } : {}), source, includeTest, variant }),
      '_blank'
    );
  };

  const sortedSites = [...sites].sort((a, b) => a.label.localeCompare(b.label));

  const handleRejectedToggle = (checked: boolean) => {
    setShowRejected(checked);
    if (!checked || rejections.length > 0 || rejectionsLoading) return;
    setRejectionsError(null);
    setRejectionsLoading(true);
    api
      .getCaptureRejections()
      .then((response) => {
        setRejections(response.rows);
        setRejectionsTotal(response.total);
      })
      .catch((err: ApiError) => setRejectionsError(err.message))
      .finally(() => setRejectionsLoading(false));
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading leads: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        kicker="Operations"
        title="Leads"
        desc="Recent captured leads across all campaigns. Read-only view — no write actions in R1."
      />

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
          {!showRejected ? (
            <div className="flex flex-wrap items-center gap-3">
              <form onSubmit={runSearch} className="flex items-center gap-2">
                <SearchInput
                  value={searchValue}
                  onChange={(value) => {
                    setSearchValue(value);
                    if (value === '' && searchActive) clearSearch();
                  }}
                  placeholder="Search by email address…"
                  className="w-72"
                />
                <Button type="submit" variant="outline" disabled={searching}>
                  {searching ? 'Searching…' : 'Search'}
                </Button>
                {searchActive ? (
                  <Button type="button" variant="ghost" onClick={clearSearch}>
                    Clear
                  </Button>
                ) : null}
              </form>
              {/* One disabled fieldset locks every filter control (preset
                  strip, date field, source select, test toggle) from the search
                  submit on, without editing the shared components. */}
              <fieldset
                aria-label="Lead filters"
                disabled={filtersLocked}
                className="m-0 flex min-w-0 flex-wrap items-center gap-3 border-0 p-0 disabled:opacity-50"
              >
                <LeadsPresetStrip preset={preset} onChange={applyPreset} />
                {preset === 'custom' && (
                  <div className="w-64">
                    <DateRangeField from={from} to={to} onChange={setRange} />
                  </div>
                )}
                <div className="w-56">
                  <Select
                    aria-label="Traffic source"
                    value={source}
                    onChange={(event) => setSource(event.target.value)}
                  >
                    <option value="">All traffic sources</option>
                    {sortedSites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.label} ({site.siteId})
                      </option>
                    ))}
                    <option value="none">No traffic source</option>
                  </Select>
                </div>
                <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />
              </fieldset>
              {EXPORT_BUTTONS.map(([variant, label]) => (
                <Button
                  key={variant}
                  type="button"
                  variant="outline"
                  data-testid={`leads-export-${variant}`}
                  disabled={filtersLocked || rangeHalfSet}
                  title={rangeHalfSet ? 'Pick both dates' : undefined}
                  onClick={() => exportLeads(variant)}
                >
                  {label}
                </Button>
              ))}
            </div>
          ) : (
            <span />
          )}
          <Toggle
            checked={showRejected}
            onChange={handleRejectedToggle}
            label="Rejected"
            hint="Show rejected captures instead of leads"
          />
        </div>
        <div className="overflow-x-auto">
          {showRejected ? (
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Campaign</th>
                  <th className="px-4 py-2.5 font-semibold">Site</th>
                  <th className="px-4 py-2.5 font-semibold">Reason</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {rejectionsLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-faint">
                      Loading...
                    </td>
                  </tr>
                ) : rejectionsError ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-destructive">
                      Error loading rejected captures: {rejectionsError}
                    </td>
                  </tr>
                ) : rejections.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-faint">
                      No rejected captures.
                    </td>
                  </tr>
                ) : (
                  rejections.map((rejection) => (
                    <tr
                      key={rejection.id}
                      className="border-b border-line/70 transition hover:bg-paper/60"
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs">{rejection.emailOriginal}</span>
                        <TestBadge isTest={rejection.isTest} />
                      </td>
                      <CampaignCell name={rejection.campaignName} linkLabel={rejection.linkLabel} />
                      <td className="px-4 py-3 font-mono text-xs text-muted">
                        {rejection.siteId || <span className="text-faint">--</span>}
                      </td>
                      <td className="px-4 py-3 text-muted">{rejection.reason}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-muted">
                        {fmtDate(rejection.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[1080px] text-left text-xs">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Campaign</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Journey</th>
                  <th className="px-4 py-2.5 font-semibold">Country</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-faint">
                      Loading...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-faint">
                      No leads found.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr
                      key={lead.id}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      className="cursor-pointer border-b border-line/70 transition hover:bg-paper/60"
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs">{lead.emailOriginal}</span>
                        <TestBadge isTest={lead.isTest} />
                      </td>
                      <CampaignCell name={lead.campaignName} linkLabel={lead.linkLabel} />
                      <td className="px-4 py-3">
                        <LookupStatusCell status={lead.beehiivLookupStatus} />
                      </td>
                      <td className="px-4 py-3">
                        <JourneyStatusCell status={lead.journeyStatus} />
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {lead.country || <span className="text-faint">--</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-muted">
                        {fmtDate(lead.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <p className="mt-3 font-mono text-xs text-faint">
        {showRejected
          ? `${rejections.length} of ${rejectionsTotal} rejected captures · newest first · dates MM-DD-YYYY`
          : `${leads.length} of ${total} leads · ${people} people${
              searchActive ? ` · filtered by "${searchValue.trim()}"` : ''
            } · newest first · dates MM-DD-YYYY`}
      </p>
    </div>
  );
}
